import React, { useState, useEffect } from 'react';
import {
    X, Eye, Code, AlertCircle, CheckCircle2,
    Variable,
    Bell
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { useClientLevel } from '../../hooks/useClientLevel';
import SingleSelectLocalSearchApiDropdown from '../../components/SingleSelectLocalSearchApiDropdown';

interface VariableDropdownProps {
    variables: { label: string; value: string }[];
    onSelect: (val: string) => void;
    disabled?: boolean;
    loading?: boolean;
}

const VariableDropdown = ({ variables, onSelect, disabled = false, loading = false }: VariableDropdownProps) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); if (!disabled) setOpen(!open); }}
                disabled={disabled}
                className={`text-[10px] bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 flex items-center gap-2 transition-all group tracking-wide font-bold uppercase shadow-sm dark:shadow-lg dark:shadow-black/20 cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <Variable className="w-3.5 h-3.5 text-orange-600 dark:text-orange-500 group-hover:text-orange-500 dark:group-hover:text-orange-400" />
                <span>{loading ? 'Loading...' : 'Insert Variable'}</span>
            </button>
            {open && !disabled && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 mt-1.5 w-38 bg-white dark:bg-[#0D1117] border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 py-2 max-h-[300px] overflow-y-auto custom-scroll ring-1 ring-black/10 dark:ring-black/50">
                        {variables.length === 0 ? (
                            <div className="px-3 py-2 text-[11px] text-gray-500 italic">No variables available</div>
                        ) : (
                            variables.map(v => (
                                <button
                                    key={v.value}
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); onSelect(v.value); setOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-[11px] text-gray-700 dark:text-gray-300 hover:bg-orange-500/10 hover:text-orange-600 dark:hover:text-white transition-all flex flex-col gap-0.5 border-b border-gray-100 dark:border-gray-800/30 last:border-0 group cursor-pointer"
                                >
                                    <span className="font-bold text-gray-800 dark:text-gray-200 group-hover:text-orange-600 dark:group-hover:text-orange-400">{v.label}</span>
                                    <span className="text-[10px] text-gray-500 dark:text-gray-400 font-mono tracking-tighter">{v.value}</span>
                                </button>
                            ))
                        )}
                    </div>
                </>
            )}
        </div>
    );
};


interface AddTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingTemplate?: any;
}


const AddTemplateModal: React.FC<AddTemplateModalProps> = ({ isOpen, onClose, onSuccess, editingTemplate }) => {
    const level = useClientLevel();
    const [name, setName] = useState('');
    const [eventType, setEventType] = useState<any>(null);
    const [ticketOpenTemplate, setTicketOpenTemplate] = useState('');
    const [ticketClosureTemplate, setTicketClosureTemplate] = useState('');
    const [isGlobal, setIsGlobal] = useState(level.name === 'global');
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'open' | 'closure'>('open');
    const [submitted, setSubmitted] = useState(false);
    const [variables, setVariables] = useState<{ label: string; value: string }[]>([]);
    const [loadingVariables, setLoadingVariables] = useState(false);

    const currentEventTypeValue = typeof eventType === 'string' ? eventType : eventType?.event_type;
    const hideClosureTab = [
        "agent_registration",
        "asset_removed",
        "new_asset_detected",
        "switch_mac_added",
        "switch_mac_removed"
    ].includes(currentEventTypeValue) && !editingTemplate?.ticket_closure_template;

    useEffect(() => {
        if (hideClosureTab && activeTab === 'closure') {
            setActiveTab('open');
        }
    }, [hideClosureTab, activeTab]);

    useEffect(() => {
        if (editingTemplate) {
            setName(editingTemplate.name || '');
            setEventType(editingTemplate.event_type ? { event_type: editingTemplate.event_type, label: editingTemplate.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) } : null);
            setTicketOpenTemplate(editingTemplate.ticket_open_template || '');
            setTicketClosureTemplate(editingTemplate.ticket_closure_template || '');
            setIsGlobal(level.name !== 'global' ? false : (editingTemplate.is_global ?? true));
        } else {
            resetForm();
        }
    }, [editingTemplate, isOpen, level.name]);

    useEffect(() => {
        if (!isOpen) {
            setVariables([]);
            return;
        }

        const fetchVariables = async () => {
            if (!eventType) {
                setVariables([]);
                return;
            }

            const currentEventType = typeof eventType === 'string' ? eventType : eventType.event_type;
            if (!currentEventType) {
                setVariables([]);
                return;
            }

            setLoadingVariables(true);
            try {
                let isCustom = eventType.is_custom;
                let moduleName = eventType.module;

                // If is_custom is undefined, fetch event types to find the match
                if (typeof isCustom === 'undefined') {
                    const typesResponse = await baseapi.get('/events/types', {
                        params: { client_id: level.id }
                    });
                    if (typesResponse.data.status && Array.isArray(typesResponse.data.data)) {
                        const found = typesResponse.data.data.find(
                            (t: any) => t.event_type === currentEventType
                        );
                        if (found) {
                            isCustom = found.is_custom;
                            moduleName = found.module;
                        }
                    }
                }

                // Now we fetch template variables
                const params: any = {
                    is_custom: isCustom ?? false,
                    page: "event_templates"
                };
                if (isCustom && moduleName) {
                    params.module_name = moduleName;
                }

                const varResponse = await baseapi.get('/events/template-variables', { params });
                if (varResponse.data.status && Array.isArray(varResponse.data.data)) {
                    setVariables(varResponse.data.data);
                } else {
                    setVariables([]);
                }
            } catch (error) {
                console.error("Error fetching template variables:", error);
                setVariables([]);
            } finally {
                setLoadingVariables(false);
            }
        };

        fetchVariables();
    }, [eventType, isOpen, level.id]);

    const validateHtml = (html: string) => {
        if (!html.trim()) return "Template cannot be empty";
        const tags = html.match(/<[a-z0-9]+[^>]*>|<\/[a-z0-9]+>/gi) || [];
        const stack: string[] = [];
        for (const tag of tags) {
            if (tag.startsWith('</')) {
                const tagName = tag.substring(2, tag.length - 1).toLowerCase();
                if (stack.length === 0 || stack.pop() !== tagName) {
                    return "Potential nesting error";
                }
            } else {
                const tagName = tag.split(/[ >]/)[0].substring(1).toLowerCase();
                if (!tag.endsWith('/>')) {
                    stack.push(tagName);
                }
            }
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
        if (!name.trim() || !eventType || !ticketOpenTemplate.trim() || (!hideClosureTab && !ticketClosureTemplate.trim())) {
            showError("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                name,
                event_type: eventType?.event_type || eventType,
                ticket_open_template: ticketOpenTemplate.replace(/"/g, "'"),
                ticket_closure_template: hideClosureTab ? "" : ticketClosureTemplate.replace(/"/g, "'"),
                is_global: isGlobal,
                is_active: true,
                client_id: isGlobal ? null : level.id
            };

            const response = editingTemplate
                ? await baseapi.put(`/event_description_templates/${editingTemplate.id}`, payload)
                : await baseapi.post('/event_description_templates', payload);

            if (response.data.status) {
                showSuccess(editingTemplate ? "Template updated successfully" : "Template added successfully");
                onSuccess();
                onClose();
                resetForm();
            } else {
                showError(response.data.message || `Failed to ${editingTemplate ? 'update' : 'add'} template`);
            }
        } catch (error: any) {
            showError(error.message || `Error ${editingTemplate ? 'updating' : 'adding'} template`);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setEventType(null);
        setTicketOpenTemplate('');
        setTicketClosureTemplate('');
        setIsGlobal(level.name === 'global');
        setSubmitted(false);
        setVariables([]);
    };

    if (!isOpen) return null;

    const currentTemplate = activeTab === 'open' ? ticketOpenTemplate : ticketClosureTemplate;

    const handleInjectVariable = (variable: string) => {
        const textarea = document.getElementById('template-editor') as HTMLTextAreaElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const scrollTop = textarea.scrollTop; // Save scroll position
        const text = textarea.value;
        const before = text.substring(0, start);
        const after = text.substring(end, text.length);
        const newValue = before + variable + after;

        if (activeTab === 'open') setTicketOpenTemplate(newValue);
        else setTicketClosureTemplate(newValue);

        // Restore focus, selection, and scroll position
        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(start + variable.length, start + variable.length);
            textarea.scrollTop = scrollTop; // Restore scroll position
        });
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-800 rounded-xl shadow-2xl w-full max-w-[100vw] h-[90vh] relative flex flex-col overflow-hidden zoom-in-95">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/50">
                    <div className="flex items-center gap-2">
                        <div className="bg-orange-500/10 dark:bg-orange-600/20 p-1.5 rounded-lg border border-orange-500/30">
                            <Code size={18} className="text-orange-600 dark:text-orange-500" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-wide">{editingTemplate ? 'Edit Event Template' : 'Add Event Template'}</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors cursor-pointer">
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-hidden flex flex-row">
                    {/* Left Side: Form and Editor */}
                    <div className="w-[45%] flex flex-col border-r border-gray-200 dark:border-gray-800 p-6 space-y-6 overflow-y-auto custom-scroll">
                        <div className="grid grid-cols-2 gap-6">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest ml-1">Template Name <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    className={`w-full bg-gray-50 dark:bg-[#0D1117] border rounded-lg px-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none transition-all font-medium ${submitted && !name.trim() ? 'border-rose-500/60 focus:border-rose-500' : 'border-gray-300 dark:border-gray-800 focus:border-orange-500/50'}`}
                                    placeholder="Enter template name..."
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                                {submitted && !name.trim() && <p className="text-[9px] text-rose-500 font-bold uppercase tracking-widest ml-1">Required</p>}
                            </div>
                            <div className="space-y-1.5">
                                <SingleSelectLocalSearchApiDropdown
                                    label="Event Type"
                                    icon={Bell}
                                    apiEndpoint="/events/types"
                                    displayProperty="label"
                                    idProperty="event_type"
                                    searchFields={['label', 'event_type']}
                                    value={eventType}
                                    onChange={(val) => {
                                        const newVal = val?.event_type;
                                        const oldVal = eventType?.event_type;
                                        if (newVal !== oldVal) {
                                            setEventType(val);
                                            setTicketOpenTemplate('');
                                            setTicketClosureTemplate('');
                                        } else {
                                            setEventType(val);
                                        }
                                    }}
                                    placeholder="Event..."
                                    searchInputPlaceholder="Search..."
                                    additionalQueries={{ client_id: level.id }}
                                    buttonClassName="!py-2.5 !text-sm"
                                />
                                {submitted && !eventType && <p className="text-[9px] text-rose-500 font-bold uppercase tracking-widest ml-1">Required</p>}
                            </div>
                        </div>

                        <div className="space-y-4 flex-1 flex flex-col min-h-0">
                            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2">
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab('open')}
                                        className={`pb-2 text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${activeTab === 'open' ? 'text-orange-600 dark:text-orange-500 border-b-2 border-orange-600 dark:border-orange-500' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                                    >
                                        Ticket Open Template
                                    </button>
                                    {!hideClosureTab && (
                                        <button
                                            type="button"
                                            onClick={() => setActiveTab('closure')}
                                            className={`pb-2 text-[10px] font-black uppercase tracking-widest transition-all cursor-pointer ${activeTab === 'closure' ? 'text-orange-600 dark:text-orange-500 border-b-2 border-orange-600 dark:border-orange-500' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                                        >
                                            Ticket Closure Template
                                        </button>
                                    )}
                                </div>
                                <VariableDropdown
                                    variables={variables}
                                    onSelect={handleInjectVariable}
                                    disabled={!eventType || loadingVariables}
                                    loading={loadingVariables}
                                />
                            </div>

                            <div className="relative flex-1 border border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50 dark:bg-gray-950 overflow-hidden flex flex-col">
                                <textarea
                                    id="template-editor"
                                    className="w-full flex-1 bg-white dark:bg-gray-950 px-4 py-4 text-xs text-gray-900 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500 font-mono focus:outline-none resize-none custom-scroll"
                                    placeholder={`Enter ${activeTab === 'open' ? 'Ticket Open' : 'Ticket Closure'} HTML/CSS code here...`}
                                    value={currentTemplate}
                                    onChange={(e) => {
                                        if (activeTab === 'open') setTicketOpenTemplate(e.target.value);
                                        else setTicketClosureTemplate(e.target.value);
                                    }}
                                />

                                <div className="p-2 border-t border-gray-200 dark:border-gray-800/50 bg-gray-50 dark:bg-gray-900/10 flex items-center justify-between">
                                    {currentTemplate && (
                                        <>
                                            {validateHtml(currentTemplate) ? (
                                                <div className="flex items-center gap-1.5 text-red-500 dark:text-red-400 text-[9px] font-black uppercase tracking-widest px-2">
                                                    <AlertCircle size={12} />
                                                    <span>Structure Warning</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 text-[9px] font-black uppercase tracking-widest px-2">
                                                    <CheckCircle2 size={12} />
                                                    <span>Valid Structure</span>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {submitted && ((activeTab === 'open' && !ticketOpenTemplate.trim()) || (activeTab === 'closure' && !ticketClosureTemplate.trim())) && (
                            <p className="text-[9px] text-rose-500 dark:text-rose-400 font-bold uppercase tracking-widest ml-1 -mt-4">This template is required</p>
                        )}

                        <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-800">
                            {level.name === 'global' && (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="is_global_checkbox"
                                        checked={isGlobal}
                                        onChange={(e) => setIsGlobal(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 text-orange-600 focus:ring-orange-500/50 cursor-pointer"
                                    />
                                    <label htmlFor="is_global_checkbox" className="text-xs font-bold text-gray-700 dark:text-gray-400 cursor-pointer">Set as Global Template</label>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side: Persistent Preview */}
                    <div className="flex-1 flex flex-col bg-gray-50/60 dark:bg-gray-950 p-6 overflow-hidden">
                        <div className="flex items-center gap-2 mb-4">
                            <Eye size={16} className="text-gray-500 dark:text-gray-400" />
                            <h3 className="text-xs font-black text-gray-700 dark:text-gray-400 uppercase tracking-widest">Preview - {activeTab === 'open' ? 'Ticket Open' : 'Ticket Closure'}</h3>
                        </div>
                        <div className="flex-1 rounded-xl bg-white border border-gray-200 dark:border-gray-800 overflow-hidden shadow-inner">
                            <iframe
                                title="Template Preview"
                                srcDoc={currentTemplate || `
                                    <!DOCTYPE html>
                                    <html>
                                        <head>
                                            <style>
                                                body { font-family: sans-serif; padding: 20px; margin: 0; color: #333; display: flex; align-items: center; justify-content: center; height: 100vh; }
                                            </style>
                                        </head>
                                        <body><div style="color: #999; font-style: italic; border: 2px dashed #eee; padding: 20px;">Start typing full HTML code (including DOCTYPE, html, body) to see preview...</div></body>
                                    </html>
                                `}
                                className="w-full h-full border-none bg-white"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex bg-gray-50 dark:bg-gray-800/80 border-t border-gray-200 dark:border-gray-800 p-4 justify-end gap-3 z-10 shadow-sm dark:shadow-2xl">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-2 text-sm font-semibold text-gray-700 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-300 dark:border-gray-700 cursor-pointer"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-8 py-2 text-sm font-bold text-white bg-orange-600 hover:bg-orange-500 rounded-lg transition-colors shadow-lg shadow-orange-900/30 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
                    >
                        {loading ? (
                            <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                        ) : (
                            editingTemplate ? "Update Template" : "Save Template"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddTemplateModal;
